import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { boxSizingStyles } from '../../styles/lit-shared';

@customElement('ui-input')
export class UiInput extends LitElement {
  static styles = [
    boxSizingStyles,
    css`
    :host {
      display: block;
    }

    .field {
      display: flex;
      flex-direction: column;
      gap: var(--space-xs);
    }

    .label-row {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: var(--space-sm);
    }

    .label {
      font-family: var(--font-sans);
      font-size: 0.8125rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--color-text-muted);
    }

    .char-count {
      font-size: 0.75rem;
      font-weight: 500;
      color: var(--color-text-subtle);
      flex-shrink: 0;
      transition: color var(--duration-fast) var(--ease-out);
    }

    .char-count--warn {
      color: var(--color-warning, #f59e0b);
    }

    .char-count--danger {
      color: var(--color-error);
      font-weight: 600;
    }

    .control {
      position: relative;
    }

    input {
      width: 100%;
      padding: 0.75rem 1rem;
      border: 1px solid var(--color-border-strong);
      border-radius: var(--radius-sm);
      background: rgb(0 0 0 / 25%);
      color: var(--color-text);
      font-family: var(--font-sans);
      font-size: 0.9375rem;
      line-height: 1.4;
      transition:
        border-color var(--duration-fast) var(--ease-out),
        box-shadow var(--duration-fast) var(--ease-out),
        background var(--duration-fast) var(--ease-out);
    }

    input::placeholder {
      color: var(--color-text-subtle);
    }

    input:hover:not(:disabled) {
      border-color: rgb(139 92 246 / 30%);
      background: rgb(0 0 0 / 35%);
    }

    input:focus {
      outline: none;
      border-color: var(--palette-violet-500);
      box-shadow:
        0 0 0 3px rgb(139 92 246 / 20%),
        0 0 24px rgb(139 92 246 / 12%);
      background: rgb(0 0 0 / 40%);
    }

    input:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }

    :host([invalid]) input {
      border-color: var(--palette-rose-500);
      box-shadow: 0 0 0 3px rgb(244 63 94 / 15%);
    }

    .hint {
      font-size: 0.8125rem;
      color: var(--color-text-subtle);
      line-height: 1.4;
    }

    .error {
      font-size: 0.8125rem;
      color: var(--color-error);
      line-height: 1.4;
    }
  `,
  ];

  @property({ type: String }) label = '';
  @property({ type: String }) type = 'text';
  @property({ type: String }) name = '';
  @property({ type: String, attribute: 'form' }) formId = '';
  @property({ type: String }) value = '';
  @property({ type: String }) placeholder = '';
  @property({ type: String }) autocomplete = '';
  @property({ type: String }) hint = '';
  @property({ type: String }) error = '';
  @property({ type: Number }) maxlength?: number;
  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) invalid = false;

  private handleInput(e: InputEvent) {
    const input = e.target as HTMLInputElement;
    this.value = input.value;
    this.dispatchEvent(
      new CustomEvent('value-changed', {
        detail: { value: this.value },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleKeydown(e: KeyboardEvent) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    this.dispatchEvent(
      new CustomEvent('submit-request', {
        bubbles: true,
        composed: true,
      }),
    );
  }

  private get charCountClass(): string {
    if (!this.maxlength) return 'char-count';
    const ratio = this.value.length / this.maxlength;
    if (ratio >= 1) return 'char-count char-count--danger';
    if (ratio >= 0.8) return 'char-count char-count--warn';
    return 'char-count';
  }

  render() {
    const inputId = `ui-input-${this.name || 'field'}`;
    const showCounter = this.maxlength !== undefined && this.value.length >= Math.floor(this.maxlength * 0.6);

    return html`
      <div class="field">
        ${this.label
          ? html`
            <div class="label-row">
              <label class="label" for=${inputId}>${this.label}</label>
              ${showCounter
                ? html`<span class=${this.charCountClass} aria-live="polite">${this.value.length}/${this.maxlength}</span>`
                : ''}
            </div>
          `
          : showCounter
            ? html`<div class="label-row"><span></span><span class=${this.charCountClass} aria-live="polite">${this.value.length}/${this.maxlength}</span></div>`
            : ''}
        <div class="control">
          <input
            id=${inputId}
            type=${this.type}
            name=${this.name}
            form=${this.formId || undefined}
            .value=${this.value}
            placeholder=${this.placeholder}
            autocomplete=${this.autocomplete}
            ?required=${this.required}
            ?disabled=${this.disabled}
            maxlength=${this.maxlength ?? ''}
            @input=${this.handleInput}
            @keydown=${this.handleKeydown}
          />
        </div>
        ${this.error
          ? html`<span class="error" role="alert">${this.error}</span>`
          : this.hint
            ? html`<span class="hint">${this.hint}</span>`
            : ''}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ui-input': UiInput;
  }
}
